import React from 'react';
import { Disc3, RotateCcw, Waves, Image as ImageIcon, Layers, Music4, Download, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SpinProvider, useSpin } from '../context/SpinContext';
import { useExportJob } from '../hooks/useExportJob';
import CollapsibleSection from '../components/spin/CollapsibleSection';
import DiscControls from '../components/spin/DiscControls';
import BackgroundControls from '../components/spin/BackgroundControls';
import ForegroundControls from '../components/spin/ForegroundControls';
import AudioControls from '../components/spin/AudioControls';
import ExportControls from '../components/spin/ExportControls';
import PresetsControls from '../components/spin/PresetsControls';
import PreviewCanvas from '../components/spin/PreviewCanvas';
import FeatureRequestButton from '../components/spin/FeatureRequestButton';
import DonateButton from '../components/spin/DonateButton';
import RenderCounter from '../components/spin/RenderCounter';
import MobileExportBar from '../components/spin/MobileExportBar';

function EditorInner() {
  const { resetAll } = useSpin();
  const exportJob = useExportJob();

  return (
    <div className="min-h-screen bg-neutral-50 pb-24 lg:pb-0">
      <h1 className="sr-only">Waxify — Free Spinning Record & Disc Video Maker for Instagram, TikTok, and YouTube</h1>
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-sm border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-neutral-900 flex items-center justify-center">
              <Disc3 className="w-[18px] h-[18px] text-white" strokeWidth={2} />
            </div>
            <span className="text-lg font-semibold text-neutral-900 tracking-tight font-display">Waxify</span>
            <span className="hidden sm:inline text-xs text-neutral-400 ml-1">turn any track into a spinning-disc video</span>
          </div>
          <div className="flex items-center gap-1">
            <FeatureRequestButton />
            <DonateButton />
            <button
              type="button"
              onClick={() => { if (window.confirm('Reset everything back to defaults?')) resetAll(); }}
              className="flex items-center justify-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800 transition-colors min-h-[40px] min-w-[40px] sm:min-h-0 sm:min-w-0 px-3 py-1.5 rounded-full hover:bg-neutral-100"
            >
              <RotateCcw className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Reset</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 py-6 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6 items-start">
        {/* Right pane comes first in DOM on mobile so preview is pinned to top */}
        <div className="order-1 lg:order-2 lg:sticky lg:top-[88px] flex justify-center w-full">
          <div className="bg-neutral-900 rounded-2xl sm:rounded-3xl p-4 sm:p-8 w-full max-w-full flex justify-center overflow-hidden">
            <PreviewCanvas />
          </div>
        </div>

        <div className="order-2 lg:order-1 space-y-3">
          <CollapsibleSection title="Account & Presets" icon={User} defaultOpen={false}>
            <PresetsControls />
          </CollapsibleSection>
          <CollapsibleSection title="Disc" icon={Disc3} defaultOpen>
            <DiscControls />
          </CollapsibleSection>
          <CollapsibleSection title="Background" icon={ImageIcon} defaultOpen={false}>
            <BackgroundControls />
          </CollapsibleSection>
          <CollapsibleSection title="Foreground" icon={Layers} defaultOpen={false}>
            <ForegroundControls />
          </CollapsibleSection>
          <CollapsibleSection title="Audio" icon={Music4} defaultOpen>
            <AudioControls />
          </CollapsibleSection>
          <CollapsibleSection title="Export" icon={Download} defaultOpen={false}>
            <ExportControls exportJob={exportJob} />
          </CollapsibleSection>
        </div>
      </main>

      <footer className="max-w-7xl mx-auto px-5 pb-10 pt-2 space-y-3">
        <p className="text-xs text-neutral-400 flex items-center gap-1.5">
          <Waves className="w-3.5 h-3.5" /> Your video is rendered on our servers and never stored longer than an hour.
        </p>
        <RenderCounter />
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-neutral-400" data-testid="homepage-seo-footer-links">
          <Link to="/free-spinning-record-video-maker" className="hover:text-neutral-700 hover:underline">Free Spinning Record Video Maker</Link>
          <Link to="/how-to-make-a-spinning-record-video" className="hover:text-neutral-700 hover:underline">How to Make a Spinning Record Video</Link>
          <Link to="/instagram-video-dimensions" className="hover:text-neutral-700 hover:underline">Instagram Video Dimensions</Link>
          <Link to="/mp3-to-video" className="hover:text-neutral-700 hover:underline">MP3 to Video</Link>
        </div>
      </footer>

      <MobileExportBar exportJob={exportJob} />
    </div>
  );
}

export default function SpinEditor() {
  return (
    <SpinProvider>
      <EditorInner />
    </SpinProvider>
  );
}
